import type { ComponentProps, ReactElement } from 'react';
import React from 'react';

import {
    HomePageBannerUpsellWrapper,
    HomePageBannerWrapper,
    HomepageValuePropCTA,
    OnsiteMessage,
} from '@zillow/zrs-cms-messaging';
import { isEmpty } from 'lodash';

import { ompNewLaneProps } from '../../constants/newLane';
import { AllHopsPlacements } from '../../types/genericTypes';

// This is a subset so we don't have to define "supportedComponents" for all homepage placements
type SupportedPlacements =
    | AllHopsPlacements.HOME_PAGE_TOP_SLOT
    | AllHopsPlacements.HOME_PAGE_BANNER;

type OnsiteMessageWrapperProps = Pick<
    ComponentProps<typeof OnsiteMessage>,
    'additionalProps' | 'messageData'
> & { placementName: SupportedPlacements };

const SupportedComponentsPerPlacement: Record<
    SupportedPlacements,
    ComponentProps<typeof OnsiteMessage>['supportedComponents']
> = {
    [AllHopsPlacements.HOME_PAGE_TOP_SLOT]: { HomepageValuePropCTA, HomePageBannerUpsellWrapper },
    [AllHopsPlacements.HOME_PAGE_BANNER]: { HomePageBannerWrapper },
};

/**
 * Use this component when rendering an OMP message for a specific placement
 * and no need for fallback
 */
const OnsiteMessageWrapper: React.FC<OnsiteMessageWrapperProps> = ({
    placementName,
    additionalProps,
    messageData,
}): ReactElement | null => {
    if (isEmpty(messageData)) {
        return null;
    }

    const triggerProps = ompNewLaneProps[placementName];

    return (
        <OnsiteMessage
            messageData={messageData}
            placementName={placementName}
            supportedComponents={SupportedComponentsPerPlacement[placementName]}
            triggerObject={triggerProps.triggerObject}
            triggerLocation={triggerProps.triggerLocation}
            triggerSource={triggerProps.triggerSource}
            additionalProps={additionalProps}
        />
    );
};

export default OnsiteMessageWrapper;
